/**
 * Portal de Usuario — Reinos & Documents catalog.
 *
 * Cada Reino tiene 4 documentos. Cada documento incluye:
 *   - `productKey`: identificador interno usado como state key.
 *   - `pdfPath`: ruta dentro del bucket privado de Cloudflare R2.
 *
 * Los PDFs se abren a través del Worker `los12-secure-reader`
 * usando `getPdfUrl(pdfPath)`.
 */

export type DocumentKind =
  | 'nucleo'
  | 'archivo_sombrio'
  | 'ritual_eclipse'
  | 'codice_compatibilidad'

export interface PortalDocument {
  kind: DocumentKind
  title: string
  description: string
  productKey: string
  /** Path inside the private R2 bucket, served by the Worker. */
  pdfPath: string
}

export interface PortalReino {
  id: string
  nombre: string
  /** Sign symbol unicode glyph. */
  symbol: string
  /** Primary atmospheric color for the Reino card. */
  color: string
  /** Background image used in the library card. */
  imageUrl: string
  documents: PortalDocument[]
}

const CDN_BASE = 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files'

// ─── Cloudflare R2 secure reader ──────────────────────────────────────
const WORKER_BASE = 'https://los12-secure-reader.los-12-account.workers.dev'

/**
 * Build the secure reader URL for a private PDF path.
 * Always go through the Worker — never expose the R2 URL directly.
 */
export const getPdfUrl = (pdfPath: string): string => {
  return `${WORKER_BASE}/?path=${encodeURIComponent(pdfPath)}`
}

// ─── Document titles (orden fijo, igual al de R2) ─────────────────────
const DOC_TITLES = {
  nucleo: {
    title: 'Núcleo del Arquetipo',
    description: 'La experiencia fundamental de tu Reino interior.',
  },
  archivo_sombrio: {
    title: 'Archivo Sombrio',
    description:
      'Los patrones ocultos, heridas y repeticiones inconscientes de tu Reino.',
  },
  ritual_eclipse: {
    title: 'Ritual del Eclipse',
    description:
      'Una práctica simbólica para integrar, liberar y transformar tu energía interna.',
  },
  codice_compatibilidad: {
    title: 'Códice de Compatibilidad',
    description:
      'La lectura de vínculos, relaciones y dinámicas entre tu Reino y otros arquetipos.',
  },
} as const

/**
 * Per-sign R2 folder/file structure.
 *
 * folder      — carpeta dentro del bucket (capitalizada)
 * upper       — versión MAYÚSCULA usada en los nombres de archivo
 * codiceLabel — capitalización exacta usada en "Codice-de-Compatibilidad <X>.pdf"
 *               (mantiene los acentos del archivo real, ej. "Cáncer")
 */
const SIGN_PATHS: Record<
  string,
  { folder: string; upper: string; codiceLabel: string }
> = {
  aries: { folder: 'Aries', upper: 'ARIES', codiceLabel: 'ARIES' },
  tauro: { folder: 'Tauro', upper: 'TAURO', codiceLabel: 'TAURO' },
  geminis: { folder: 'Geminis', upper: 'GEMINIS', codiceLabel: 'GÉMINIS' },
  cancer: { folder: 'Cancer', upper: 'CANCER', codiceLabel: 'Cáncer' },
  leo: { folder: 'Leo', upper: 'LEO', codiceLabel: 'Leo' },
  virgo: { folder: 'Virgo', upper: 'VIRGO', codiceLabel: 'Virgo' },
  libra: { folder: 'Libra', upper: 'LIBRA', codiceLabel: 'LIBRA' },
  escorpio: {
    folder: 'Escorpio',
    upper: 'ESCORPIO',
    codiceLabel: 'Escorpio',
  },
  sagitario: {
    folder: 'Sagitario',
    upper: 'SAGITARIO',
    codiceLabel: 'Sagitario',
  },
  capricornio: {
    folder: 'Capricornio',
    upper: 'CAPRICORNIO',
    codiceLabel: 'CAPRICORNIO',
  },
  acuario: { folder: 'Acuario', upper: 'ACUARIO', codiceLabel: 'Acuario' },
  piscis: { folder: 'Piscis', upper: 'PISCIS', codiceLabel: 'PISCIS' },
}

function buildDocs(reinoId: string): PortalDocument[] {
  const paths = SIGN_PATHS[reinoId]
  if (!paths) {
    // Fallback shouldn't happen — guarded by type but keep safe.
    return []
  }
  const { folder, upper, codiceLabel } = paths
  return [
    {
      kind: 'nucleo',
      ...DOC_TITLES.nucleo,
      productKey: `${reinoId}_nucleo`,
      pdfPath: `${folder}/${upper}.pdf`,
    },
    {
      kind: 'archivo_sombrio',
      ...DOC_TITLES.archivo_sombrio,
      productKey: `${reinoId}_archivo_sombrio`,
      pdfPath: `${folder}/ARCHIVO-SOMBRIO - ${upper}.pdf`,
    },
    {
      kind: 'ritual_eclipse',
      ...DOC_TITLES.ritual_eclipse,
      productKey: `${reinoId}_ritual_eclipse`,
      pdfPath: `${folder}/RITUAL-DEL-ECLIPSE-${upper}.pdf`,
    },
    {
      kind: 'codice_compatibilidad',
      ...DOC_TITLES.codice_compatibilidad,
      productKey: `${reinoId}_codice_compatibilidad`,
      pdfPath: `${folder}/Codice-de-Compatibilidad ${codiceLabel}.pdf`,
    },
  ]
}

export const REINOS: PortalReino[] = [
  {
    id: 'aries',
    nombre: 'Aries',
    symbol: '♈',
    color: '#C41E3A',
    imageUrl: `${CDN_BASE}/01_ARIES_1.png?v=1778615905`,
    documents: buildDocs('aries'),
  },
  {
    id: 'tauro',
    nombre: 'Tauro',
    symbol: '♉',
    color: '#4A7C3A',
    imageUrl: `${CDN_BASE}/02_TAURO_1.png`,
    documents: buildDocs('tauro'),
  },
  {
    id: 'geminis',
    nombre: 'Géminis',
    symbol: '♊',
    color: '#E6C200',
    imageUrl: `${CDN_BASE}/03_GEMINIS_1.png`,
    documents: buildDocs('geminis'),
  },
  {
    id: 'cancer',
    nombre: 'Cáncer',
    symbol: '♋',
    color: '#9CB4D8',
    imageUrl: `${CDN_BASE}/04_CANCER_1.png`,
    documents: buildDocs('cancer'),
  },
  {
    id: 'leo',
    nombre: 'Leo',
    symbol: '♌',
    color: '#E6B800',
    imageUrl: `${CDN_BASE}/05_LEO_1.png`,
    documents: buildDocs('leo'),
  },
  {
    id: 'virgo',
    nombre: 'Virgo',
    symbol: '♍',
    color: '#D4D4C8',
    imageUrl: `${CDN_BASE}/06_VIRGO_1.png`,
    documents: buildDocs('virgo'),
  },
  {
    id: 'libra',
    nombre: 'Libra',
    symbol: '♎',
    color: '#E8C5C9',
    imageUrl: `${CDN_BASE}/07LIBRA01_1.png?v=1778623066`,
    documents: buildDocs('libra'),
  },
  {
    id: 'escorpio',
    nombre: 'Escorpio',
    symbol: '♏',
    color: '#6B2C91',
    imageUrl: `${CDN_BASE}/08_ESCORPIO_1.png`,
    documents: buildDocs('escorpio'),
  },
  {
    id: 'sagitario',
    nombre: 'Sagitario',
    symbol: '♐',
    color: '#3D5AC9',
    imageUrl: `${CDN_BASE}/09_SAGITARIO_1.png`,
    documents: buildDocs('sagitario'),
  },
  {
    id: 'capricornio',
    nombre: 'Capricornio',
    symbol: '♑',
    color: '#5C6B73',
    imageUrl: `${CDN_BASE}/010CAPRICORNIO_1.png?v=1778623139`,
    documents: buildDocs('capricornio'),
  },
  {
    id: 'acuario',
    nombre: 'Acuario',
    symbol: '♒',
    color: '#1FA8DC',
    imageUrl: `${CDN_BASE}/011ACUARIO_1.png?v=1778623164`,
    documents: buildDocs('acuario'),
  },
  {
    id: 'piscis',
    nombre: 'Piscis',
    symbol: '♓',
    color: '#4682B4',
    imageUrl: `${CDN_BASE}/012PISCIS.png?v=1778623410`,
    documents: buildDocs('piscis'),
  },
]

export const getReinoById = (id: string) =>
  REINOS.find((r) => r.id === id) || null
